import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  BlockStack,
  Text,
  Banner,
  Spinner,
  List,
  Collapsible,
  Box,
  Button,
} from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
import { checkProductSchema, getFirstProductStorefrontUrl } from '../../services/api';
import type { ProductSchemaCheckResponse } from '../../services/api';

/**
 * ProductSchemaChecker — auto-detects duplicate JSON-LD Product schemas on the
 * store's first product page.
 *
 * 1. Fetches the first product's storefront URL via GET /api/products?page=1&limit=1.
 * 2. Passes that URL to GET /api/feed/check-product-schema.
 * 3. If both the theme and Zelium inject a Product schema, shows a warning with
 *    per-theme instructions for disabling the theme's built-in JSON-LD.
 */
export function ProductSchemaChecker() {
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  // Step 1: resolve the first product's public URL
  const { data: productUrl, isLoading: urlLoading } = useQuery<string | null>({
    queryKey: ['first-product-storefront-url'],
    queryFn: getFirstProductStorefrontUrl,
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Step 2: run schema check once we have a URL
  const { data: result, isLoading: checking } = useQuery<ProductSchemaCheckResponse>({
    queryKey: ['product-schema-check', productUrl],
    queryFn: () => checkProductSchema(productUrl!),
    enabled: !!productUrl,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const isLoading = urlLoading || (!!productUrl && checking);

  // Don't render the card at all when everything is clean and no issue found
  const shouldHide =
    result?.ok &&
    !result.hasDuplicate &&
    result.zeliumJsonLd &&
    !result.themeJsonLd;

  if (!isLoading && shouldHide) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingSm">JSON-LD Schema Conflict Check</Text>

        {isLoading && (
          <BlockStack gap="200">
            <Spinner size="small" />
            <Text as="p" variant="bodySm" tone="subdued">Checking your storefront for duplicate schemas…</Text>
          </BlockStack>
        )}

        {!isLoading && result && (
          <CheckResult
            result={result}
            productUrl={productUrl ?? undefined}
            guidelinesOpen={guidelinesOpen}
            onToggleGuidelines={() => setGuidelinesOpen((v) => !v)}
          />
        )}

        {!isLoading && !result && !productUrl && (
          <Text as="p" variant="bodySm" tone="subdued">
            No products found in your store yet. Run a scan first to enable this check.
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}

function CheckResult({
  result,
  productUrl,
  guidelinesOpen,
  onToggleGuidelines,
}: Readonly<{
  result: ProductSchemaCheckResponse;
  productUrl?: string;
  guidelinesOpen: boolean;
  onToggleGuidelines: () => void;
}>) {
  if (!result.ok) {
    return (
      <Banner tone="critical" icon={AlertTriangleIcon}>
        <p>{result.error ?? 'Could not fetch the product page.'}</p>
      </Banner>
    );
  }

  if (result.hasDuplicate) {
    return (
      <BlockStack gap="300">
        <Banner tone="warning" icon={AlertTriangleIcon} title="Duplicate Product Schema detected">
          <p>
            Both your <strong>theme</strong> and <strong>Zelium</strong> are injecting a{' '}
            <code>Product</code> JSON-LD schema ({result.productSchemaCount} schemas found
            {productUrl ? (
              <> on <strong>{productUrl}</strong></>
            ) : null}
            ). Duplicate schemas can cause AI engines and search crawlers to receive conflicting
            product data, lowering discoverability and score accuracy.
          </p>
          <p>
            <strong>Action required:</strong> Disable your theme&apos;s built-in JSON-LD output
            so only the Zelium-enriched schema is served.
          </p>
        </Banner>

        <Button
          variant="plain"
          onClick={onToggleGuidelines}
          ariaExpanded={guidelinesOpen}
          ariaControls="schema-guidelines"
        >
          {guidelinesOpen ? 'Hide' : 'Show'} how to fix this in your theme
        </Button>

        <Collapsible id="schema-guidelines" open={guidelinesOpen} transition={{ duration: '200ms' }}>
          <Box paddingBlockStart="200">
            <BlockStack gap="400">
              <Text as="p" variant="bodySm" tone="subdued">
                Every Shopify theme stores this setting differently. Find your theme below.
              </Text>

              <ThemeGuidelineSection
                theme="Dawn (and most free Shopify themes)"
                steps={[
                  'In Shopify Admin go to Online Store → Themes → Customize.',
                  'Open Theme settings (bottom-left gear icon).',
                  'Find the "SEO" or "Search engine" section.',
                  'Disable "Product structured data" or "Schema.org markup".',
                  'Save and reload this page to re-check.',
                ]}
              />

              <ThemeGuidelineSection
                theme="Debut / Brooklyn / Minimal"
                steps={[
                  'Go to Online Store → Themes → Customize → Theme settings.',
                  'Find the "SEO" panel and toggle off "Product schema markup".',
                  'For older versions: in the code editor open product.liquid and remove the {% render "product-schema" %} or {% include "product-json-ld" %} tag.',
                ]}
              />

              <ThemeGuidelineSection
                theme="Impulse / Prestige / Pipeline (premium themes)"
                steps={[
                  'Go to Customize → Theme settings → Advanced / SEO.',
                  'Disable "JSON-LD structured data", "Schema markup", or "Rich snippets".',
                  'Alternative: in the code editor open snippets/product-schema.liquid and wrap the block in {% comment %} … {% endcomment %}.',
                ]}
              />

              <ThemeGuidelineSection
                theme="Turbo / Empire (Out of the Sandbox)"
                steps={[
                  'Open Customize → General settings → Advanced.',
                  'Find "JSON-LD" or "Structured data" and uncheck it.',
                  'Save and confirm by reloading this page.',
                ]}
              />

              <ThemeGuidelineSection
                theme="Any theme — universal code-editor fallback"
                steps={[
                  'Go to Online Store → Themes → Edit code.',
                  'Use the search box to find "application/ld+json" across all files.',
                  'Locate the <script> block whose body contains "@type": "Product" and does NOT have data-zelium on the tag.',
                  'Wrap the entire <script>…</script> block in a Liquid comment: {% comment %} … {% endcomment %}.',
                  'Save, then reload this page to confirm the warning disappears.',
                ]}
              />

              <Banner tone="info">
                <p>
                  Not sure which file? In <strong>Online Store → Themes → Edit code</strong>,
                  search for <code>application/ld+json</code>. The script containing{' '}
                  <code>&quot;@type&quot;: &quot;Product&quot;</code> without a{' '}
                  <code>data-zelium</code> attribute is the one to disable.
                </p>
              </Banner>
            </BlockStack>
          </Box>
        </Collapsible>
      </BlockStack>
    );
  }

  if (!result.zeliumJsonLd && result.themeJsonLd) {
    return (
      <Banner tone="info" title="Zelium schema not detected">
        <p>
          A theme-generated <code>Product</code> schema was found but no Zelium schema. Enable
          the <strong>Zelium AI Structured Data</strong> app embed under{' '}
          <strong>Online Store → Themes → Customize → App embeds</strong>.
        </p>
      </Banner>
    );
  }

  if (!result.zeliumJsonLd && !result.themeJsonLd) {
    return (
      <Banner tone="info" title="No Product schema found">
        <p>
          No <code>Product</code> JSON-LD was detected. Confirm the Zelium app embed is active
          and the store is publicly accessible.
        </p>
      </Banner>
    );
  }

  // zeliumJsonLd && !themeJsonLd → clean, card hidden by parent
  return null;
}

function ThemeGuidelineSection({ theme, steps }: Readonly<{ theme: string; steps: string[] }>) {
  return (
    <BlockStack gap="150">
      <Text as="p" variant="bodyMd" fontWeight="semibold">{theme}</Text>
      <List type="number">
        {steps.map((step) => (
          <List.Item key={step}>{step}</List.Item>
        ))}
      </List>
    </BlockStack>
  );
}
